// Globals
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { LinkOutlined } from '@ant-design/icons';

// Store
import { useAppDispatch } from '@/store';
import { getExportFeeds } from '../../feature';

// Models
import { FeedGenerationStatuses, IFeed } from '@/models/feeds.model';
import { SupportedLocales } from '@/models/localization.model';

// Components
import { Modal, ModalSize } from '@/components';
import { CrossIcon, DownloadIcon, EditIcon } from '@/icons';
import { COLORS } from '@/theme';
import { Button } from '@/ui';
import {
  StyledActionsWrap,
  StyledButtonsWrapper,
  StyledTitleModal,
  StyledActionButton,
  StyledLink,
} from './TableActionsStyles';

// Helpers
import { useModal } from '@/hooks';
import { fileDownload, toNumber } from '@/utils';

type TTableActions = {
  record: IFeed;
  onEdit: (item: IFeed) => void;
  onDelete: (item: IFeed) => void;
};

const TableActions = ({ onDelete, onEdit, record }: TTableActions) => {
  const { t } = useTranslation(['seo']);
  const { isOpened, openModal, closeModal } = useModal();
  const handleEdit = () => onEdit(record);
  const handleDelete = () => onDelete(record);
  const dispatch = useAppDispatch();
  const isFeedActive = record?.generation_status === FeedGenerationStatuses.FINISHED && record?.active;

  const handleExport = useCallback(async () => {
    try {
      const downloadFilename = record?.files?.uk?.split('/').pop() || 'file.xml';
      const file = await dispatch(
        getExportFeeds({
          feed_id: toNumber(record.id),
          locale: SupportedLocales.UK,
        }),
      ).unwrap();

      fileDownload(file, downloadFilename, 'xml');
    } catch (error) {}
  }, [dispatch, record]);

  return (
    <StyledActionsWrap>
      <StyledActionButton onClick={handleEdit} $color={COLORS.GREEN}>
        <EditIcon />
      </StyledActionButton>
      <StyledActionButton $color={COLORS.RED} onClick={openModal}>
        <CrossIcon />
      </StyledActionButton>
      {isFeedActive && (
        <>
          <StyledLink href={record?.files?.uk} target="_blank">
            <LinkOutlined />
          </StyledLink>
          <StyledActionButton $color={COLORS.BLUE} onClick={handleExport}>
            <DownloadIcon />
          </StyledActionButton>
        </>
      )}
      <Modal visible={isOpened} onCancel={closeModal} size={ModalSize.LARGE}>
        <StyledTitleModal>{t('feeds_delete_title')}</StyledTitleModal>
        <StyledButtonsWrapper>
          <Button onClick={closeModal} type="primary">
            {t('feeds_delete_cancel')}
          </Button>
          <Button onClick={handleDelete} type="text">
            {t('feeds_delete_ok')}
          </Button>
        </StyledButtonsWrapper>
      </Modal>
    </StyledActionsWrap>
  );
};

export default TableActions;
